import React, { useState, useMemo, useRef, useCallback } from 'react';
import { StyleSheet, Platform, View, ActionSheetIOS, Linking } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import useTranslation from '@/hooks/useTranslation';
import Text from '@/components/Typography/Text';
import Badge from '@/components/Typography/Badge';
import * as DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import DropdownMenuItem from '@/components/DropdownMenu/DropdownMenuItem';
import { LOCAL_TOOLTIP_NOTIS, MAX_FILES_UPLOADED_IN_MESSAGE } from '@/constants/config';
import DropdownMenuItemDivider from '@/components/DropdownMenu/DropdownMenuItemDivider';
import { useModalStore, useFileStore, useChatInputStore, useAuthStore, useToastStore, useModelStore, useUIStore } from '@/store';
import { uploadFileWithPlaceholder, uploadFilesWithPlaceholders } from '@/lib/uploadFileWithPlaceholder';
import useResponsiveness from '@/hooks/useResponsiveness';
import useRequireAuth from '@/hooks/useRequireAuth';
import Tooltip from '@/components/Tooltip';
import { shouldShowTooltipNoti, hasSeenTooltipNoti, markTooltipNotiSeen } from '@/constants/utils/tooltip_notifications';
import { areTextToolsDisabledForSelection } from '@/constants/utils/tools';
import NumberControl from './CustomParameterOptions/NumberControl';
import FileUploadControl from './CustomParameterOptions/FileUploadControl';
import ToggleControl from './CustomParameterOptions/ToggleControl';
import DateRangeControl from './CustomParameterOptions/DateRangeControl';
import TextareaControl from './CustomParameterOptions/TextareaControl';
import TextareaMultiControl from './CustomParameterOptions/TextareaMultiControl';
import ParameterButton from './CustomParameterOptions/ParameterButton';
import DropdownHeader from './CustomParameterOptions/DropdownHeader';
import * as DocumentPicker from 'expo-document-picker';
import * as ImagePicker from 'expo-image-picker';

const createStyles = (colors) => StyleSheet.create({
  dropdownContent: {
    minWidth: 220,
    maxWidth: 400,
  },
  verticalDivider: {
    width: 1,
    height: 12,
    backgroundColor: colors.text_color_primary,
    opacity: 0.25,
    alignSelf: 'center',
  },
  titleContainer: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    gap: 8,
  },
  titleContainerBadge: {
    marginLeft: 'auto',
  },
  doneButton: {
    paddingHorizontal: 10,
    paddingBottom: 5,
    marginLeft: 'auto',
    display: 'flex',
    minWidth: 100,
    maxWidth: 100,
  },
  // Added - selected action container row
  selectedActionContainer: {
    flexDirection: 'row',
    gap: 6,
    alignItems: 'center',
  },
  // Added - small container for Done button in toggle
  doneButtonContainerSmall: {
    marginTop: 10,
    display: 'flex',
    width: 200,
    marginLeft: 'auto',
  },
});

// Web-only style helpers and reusable Done button style overrides
const WEB_HIDDEN_INPUT_STYLE = { display: 'none' };
const DONE_BUTTON_MARGIN_TOP_STYLES = { doneButton: { marginTop: 10 } };

// Wrap children with Tooltip only if a label exists (web-only handled inside Tooltip)
const TriggerWithTooltip = ({ label, children, tooltipProps = {} }) => {
  return label ? (
    <Tooltip
      label={label}
      {...tooltipProps}
    >
      {children}
    </Tooltip>
  ) : (
    children
  );
};


const DoneButton = ({ title, onSelect, styles, isSecondary }) => {
  return (
    <View style={styles.doneButton}>
      <DropdownMenuItem title={title} onSelect={onSelect} isSecondary={isSecondary} isTitleCentered />
    </View>
  );
};

/** Param component types that have a detail view in ChatInputCustomParametersModal (open that on native instead of dropdown).
 *  Zeego's native menus cannot render custom React components, so all param types with rich UI must go through the modal. */
const PARAM_TYPES_USE_MODAL_ON_NATIVE = ['select', 'action', 'multiselect', 'daterange', 'textarea-multi', 'textarea', 'number', 'slider', 'toggle', 'file', 'image', 'audio'];

const ChatInputCustomParameterButton = ({ 
  icon, 
  text, 
  onPress,
  parameterData = null,
  selectedParameters = {},
  selectedAgents = [],
  selectedModels = [],
  selectedAgentIds = [],
  selectedModelIds = [],
  onAddFile,
  onAddUploadingFile,
  onChange,
  files = [],
  shouldShowDone = false,
  doneLabel = 'Done',
  isHighlighted = false,
  /** On native, open this param (or null for full list) in ChatInputCustomParametersModal instead of dropdown. */
  onOpenDetailModal,
}) => {
  const { colors } = useTheme();
  const { isXS } = useResponsiveness();
  const { t } = useTranslation();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const [paramValue, setParamValue] = useState(parameterData?.value);
  const model = parameterData?.modelId ? useModelStore.getState().getModelById(parameterData.modelId) : null;
  
  const displayName = useMemo(() => {
    const name = parameterData?.ui?.displayName;
    // Check if more than one model is selected before appending the model name
    if (model?.name && name && selectedModels?.length > 1) {
      return `${name} (${model.name})`;
    }
    return name;
  }, [parameterData?.ui?.displayName, model, selectedModels?.length]);

  const [isMainButtonHovered, setIsMainButtonHovered] = useState(false);
  const [isSelectedActionHovered, setIsSelectedActionHovered] = useState(false);
  const { openModal, closeModal } = useModalStore(state => state.actions);
  const { uploadFile, isFileAllowed, fetchAllowedFileTypes, hasCreditsForFileUpload } = useFileStore(state => state.actions);
  const { addToast } = useToastStore(state => state.actions);
  const fileInputRef = useRef(null);
  const requireAuth = useRequireAuth();
  const isPlus = useAuthStore(state => state.user?.flags?.isPlus);
  const setInputPlaceholder = useChatInputStore(state => state.setInputPlaceholder);
  const canUseScreenCapture = useMemo(() => {
    if (Platform.OS !== 'web') return false;
    try {
      return !!(typeof navigator !== 'undefined' && navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia);
    } catch (_) {
      return false;
    }
  }, []);

  const [hasSeenNewToolsTooltip, setHasSeenNewToolsTooltip] = useState(() => {
    if (Platform.OS !== 'web') return true;
    return hasSeenTooltipNoti(LOCAL_TOOLTIP_NOTIS.TOOLS_NEW_10022025);
  });

  const toolsNoti = LOCAL_TOOLTIP_NOTIS.TOOLS_NEW_10022025;

  // Single-source-of-truth capability logic:
  // Determine if text tools (search-web, study-and-learn) should be disabled
  // based on the currently selected models and agents.
  const areTextToolsDisabled = useMemo(
    () => areTextToolsDisabledForSelection({ selectedModels, selectedAgents }),
    [selectedModels, selectedAgents]
  );

  // Sync local param value with store-driven selectedParameters
  React.useEffect(() => {
    if (!parameterData?.id) return;
    const externalValue = selectedParameters?.[parameterData.id];
    setParamValue(externalValue ?? null);
  }, [selectedParameters, parameterData?.id]);
  
  const hasReachedFileLimit = files?.length >= MAX_FILES_UPLOADED_IN_MESSAGE;
  
  // Validation helpers for showing red dot on parameter button
  const isEmptyValue = (val) => {
    if (val === null || val === undefined) return true;
    if (typeof val === 'string') return val.trim().length === 0;
    if (Array.isArray(val)) return val.length === 0 || val.every((v) => v === null || v === undefined || v === '');
    if (typeof val === 'object') return Object.keys(val).length === 0;
    return false;
  };
  const validateByType = (val) => {
    if (!parameterData?.validation) return false;

    const v = val;

    // Options validation: ensure value(s) exist in provided options list
    const opts = parameterData?.validation?.options;
    if (Array.isArray(opts) && opts.length > 0) {
      const allowed = new Set(opts.map(o => o?.value));
      const comp = parameterData?.ui?.component;
      if (comp === 'multiselect') {
        const arr = Array.isArray(v) ? v : (v !== undefined && v !== null && v !== '' ? [v] : []);
        if (arr.length > 0 && !arr.every(item => allowed.has(item))) return true;
      } else if (comp === 'select' || comp === 'action') {
        if (v !== undefined && v !== null && v !== '' && !allowed.has(v)) {
          return true;
        } else {
          return false;
        }
      }
    }

    // String length
    const minLen = parameterData?.validation?.string?.minLength;
    const maxLen = parameterData?.validation?.string?.maxLength;
    if (typeof v === 'string' && minLen !== undefined && maxLen !== undefined) {
      const len = v.length;
      if (typeof minLen === 'number' && len < minLen) {
        return true;
      }
      if (typeof maxLen === 'number' && len > maxLen) {
        return true;
      }
    }
    // Number range
    const min = parameterData?.validation?.number?.min;
    const max = parameterData?.validation?.number?.max;
    if (typeof v === 'number') {
      if (typeof min === 'number' && v < min) return true;
      if (typeof max === 'number' && v > max) return true;
    }
    // Daterange: expect [start, end] both truthy strings if present and/or required
    if (parameterData?.ui?.component === 'daterange') {
      if (Array.isArray(v)) {
        const [s, e] = v;
        // If one side exists but not the other, consider invalid
        if ((s && !e) || (!s && e)) return true;
      }
    }

    // textarea-multi object: if required, ensure at least one non-empty field; type-specific min/max not defined
    return false;
  };
  const isRequired = !!parameterData?.validation?.required;
  const requiredUnmet = isRequired && isEmptyValue(paramValue);
  const typeInvalid = validateByType(paramValue);
  const showValidationDot = requiredUnmet || typeInvalid;
  const isRequirementValid = isRequired && !requiredUnmet && !typeInvalid;

  // Check if files button should be disabled based on selected action
  const isFilesButtonDisabled = useMemo(() => {
    const selectedAction = selectedParameters?.action;
    return ['create-image', 'search'].includes(selectedAction) || hasReachedFileLimit;
  }, [selectedParameters?.action, hasReachedFileLimit]);

  // Get the selected option details if this is an action parameter
  const selectedActionOption = useMemo(() => {
    if (parameterData?.id === 'action' && selectedParameters?.action) {
      return parameterData.validation.options.find(
        opt => opt.value === selectedParameters.action
      );
    }
    return null;
  }, [parameterData, selectedParameters]);

  // Tracks whether THIS component cleared search-web because tools became
  // disabled. The restore effect below only undoes clears it performed —
  // otherwise it would fight collection defaults that explicitly seeded
  // tools without web search.
  const clearedSearchWebForNonTextRef = useRef(false);

  // When tools become disabled due to non-text selections, proactively
  // clear text tools from the current selection so they don't remain active.
  // Do NOT sync the cleared state into the persisted webSearchEnabled
  // preference — switching to an image/video model is not the user turning
  // Search Web off for future chats.
  React.useEffect(() => {
    if (!parameterData || parameterData.id !== 'tools') return;
    if (!areTextToolsDisabled) return;

    const current = Array.isArray(paramValue)
      ? paramValue
      : (paramValue ? [paramValue] : []);

    const filtered = current.filter(
      (v) => v !== 'search-web' && v !== 'study-and-learn'
    );

    if (filtered.length === current.length) {
      // Nothing to clear
      return;
    }

    if (current.includes('search-web')) {
      clearedSearchWebForNonTextRef.current = true;
    }

    const next = filtered.length ? filtered : null;
    setParamValue(next);
    const key = parameterData.id || parameterData.name;
    const options = { syncWebSearchPreference: false };
    if (onChange) onChange(key, next, options);
    else if (onPress) onPress(key, next, options);
  }, [areTextToolsDisabled, parameterData, paramValue, onChange, onPress]);

  // When returning to a text-capable selection, restore Search Web from the
  // persisted preference — but only if the effect above cleared it, so
  // collection defaults and manual per-chat choices are never overridden.
  React.useEffect(() => {
    if (!parameterData || parameterData.id !== 'tools') return;
    if (areTextToolsDisabled) return;
    if (!clearedSearchWebForNonTextRef.current) return;
    clearedSearchWebForNonTextRef.current = false;
    if (!useUIStore.getState().webSearchEnabled) return;

    const current = Array.isArray(paramValue)
      ? paramValue
      : (paramValue ? [paramValue] : []);
    if (current.includes('search-web')) return;

    const next = [...current, 'search-web'];
    setParamValue(next);
    const key = parameterData.id || parameterData.name;
    const options = { syncWebSearchPreference: false };
    if (onChange) onChange(key, next, options);
    else if (onPress) onPress(key, next, options);
  }, [areTextToolsDisabled, parameterData, paramValue, onChange, onPress]);

  const handleValueChange = (newValue) => {
    setParamValue(newValue);
    const key = parameterData?.id || parameterData?.name;
    if (onChange) onChange(key, newValue);
    else if (onPress) onPress(key, newValue);
  };

  // Native file upload helper — processes picked files, creates temp entries, uploads
  const handleNativeFileUpload = useCallback(async (pickedFiles) => {
    await uploadFilesWithPlaceholders(pickedFiles, {
      uploadFile,
      isFileAllowed,
      addToast,
      onAddUploadingFile,
      onAddFile,
      t,
    });
  }, [isFileAllowed, uploadFile, addToast, onAddFile, onAddUploadingFile, t]);

  const handleSelect = async (value, close) => {
    // Get the selected option
    const option = parameterData.validation.options.find(opt => opt.value === value);
      
    // Handle file-related actions
    if (parameterData?.id === 'files') {
      // Anonymous visitors can't upload/screenshot/select-from-library —
      // gate before we touch native pickers or the screen-capture API.
      const isFileAction = value === 'file-upload' || value === 'file-screenshot' || value === 'file-library';
      if (isFileAction) {
        const gated = requireAuth(() => true, {
          source: `composer_param_${value}`,
          title: t('chatInput.authCta.uploadTitle'),
          subtitle: t('chatInput.authCta.uploadSubtitle'),
          icon: value === 'file-screenshot' ? 'camera' : value === 'file-library' ? 'folder-open' : 'paperclip',
        });
        if (gated !== true) { close?.(); return; }
      }

      console.log('isFilesButtonDisabled', isFilesButtonDisabled);
      if (hasReachedFileLimit) {
        addToast(t('chatInput.maxFilesPerMessage').replace('%MAX%', MAX_FILES_UPLOADED_IN_MESSAGE), 'error');
        close?.();
        return;
      }
      if (isFilesButtonDisabled) return; // Prevent file actions when disabled
      
      if (value === 'file-upload') {
        // Check if user has credits before allowing file upload
        const { hasCredits, message: creditsMessage } = await hasCreditsForFileUpload();
        if (!hasCredits) {
          addToast(creditsMessage, 'error');
          close?.();
          return;
        }
        // Ensure allowed file types are loaded for validation
        if (Platform.OS !== 'web') {
          try {
            await fetchAllowedFileTypes();
          } catch (error) {
            console.error('[FileUpload] Failed to fetch allowed file types:', error);
            addToast(t('chatInput.unableToVerifyFileTypes'), 'error');
          }
        }
        if (Platform.OS === 'web') {
          // Web: Trigger hidden file input click
          fileInputRef.current?.click();
        } else if (Platform.OS === 'ios') {
          // iOS: Show native action sheet to pick files/photos
          ActionSheetIOS.showActionSheetWithOptions(
            {
              options: [t('chatInput.files.actionSheetCancel'), t('chatInput.files.actionSheetTakePhoto'), t('chatInput.files.actionSheetChooseFromLibrary'), t('chatInput.files.actionSheetBrowseFiles')],
              cancelButtonIndex: 0,
            },
            async (buttonIndex) => {
              try {
                if (buttonIndex === 1) {
                  // Take photo
                  const permission = await ImagePicker.requestCameraPermissionsAsync();
                  if (permission.status !== 'granted') {
                    if (permission.canAskAgain === false) {
                      addToast(t('chatInput.files.cameraDisabled'), 'error');
                      await Linking.openSettings().catch((error) => {
                        console.warn('[FileUpload] Failed to open settings:', error);
                      });
                      return;
                    }
                    addToast(t('chatInput.files.cameraPermission'), 'error');
                    return;
                  }
                  const result = await ImagePicker.launchCameraAsync({ mediaTypes: ['images'], quality: 0.8 });
                  if (result.canceled) return;
                  const asset = result.assets[0];
                  await handleNativeFileUpload([{
                    uri: asset.uri,
                    name: asset.fileName || `camera_photo_${Date.now()}.jpg`,
                    size: asset.fileSize || 0,
                    type: asset.mimeType || 'image/jpeg',
                  }]);
                } else if (buttonIndex === 2) {
                  // Choose from library
                  const result = await ImagePicker.launchImageLibraryAsync({
                    mediaTypes: ['images', 'videos'],
                    allowsMultipleSelection: true,
                    quality: 0.8,
                  });
                  if (result.canceled) return;
                  const files = result.assets.map(a => ({
                    uri: a.uri,
                    name: a.fileName || a.uri.split('/').pop() || 'photo.jpg',
                    size: a.fileSize || 0,
                    type: a.mimeType || (a.type === 'video' ? 'video/mp4' : 'image/jpeg'),
                  }));
                  await handleNativeFileUpload(files);
                } else if (buttonIndex === 3) {
                  // Browse files
                  const result = await DocumentPicker.getDocumentAsync({ multiple: true, copyToCacheDirectory: true });
                  if (result.canceled) return;
                  const files = result.assets.map(a => ({
                    uri: a.uri,
                    name: a.name,
                    size: a.size,
                    type: a.mimeType || 'application/octet-stream',
                  }));
                  await handleNativeFileUpload(files);
                }
              } catch (error) {
                console.error('Error picking file on iOS:', error);
                addToast(t('chatInput.files.pickFileFailed'), 'error');
              }
            }
          );
        } else {
          try {
            const result = await DocumentPicker.getDocumentAsync({ multiple: true, copyToCacheDirectory: true });
            if (!result.canceled) {
              const files = result.assets.map(a => ({
                uri: a.uri,
                name: a.name,
                size: a.size,
                type: a.mimeType || 'application/octet-stream',
              }));
              await handleNativeFileUpload(files);
            }
          } catch (error) {
            console.error('Error picking file:', error);
            addToast(t('chatInput.files.pickFileFailed'), 'error');
          }
        }
        return;
      } else if (value === 'file-screenshot') {
        // Check if user has credits before allowing screenshot capture
        const { hasCredits, message: creditsMessage } = await hasCreditsForFileUpload();
        if (!hasCredits) {
          addToast(creditsMessage, 'error');
          close?.();
          return;
        }
        if (!canUseScreenCapture) {
          addToast(t('chatInput.files.screenCaptureNotSupported'), 'error');
          close?.();
          return;
        }
        // Use Screen Capture API to take a screenshot
        (async () => {
          try {
            // Ensure allowed types are loaded so validation works
            try {
              await fetchAllowedFileTypes();
            } catch (error) {
              console.error('[FileUpload] Failed to fetch allowed file types for screenshot:', error);
              addToast(t('chatInput.unableToVerifyFileTypes'), 'error');
            }

            const stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
            const track = stream.getVideoTracks && stream.getVideoTracks()[0];

            const video = document.createElement('video');
            video.style.position = 'fixed';
            video.style.top = '-10000px';
            video.style.left = '-10000px';
            video.srcObject = stream;

            await new Promise((resolve) => {
              const onLoaded = () => resolve();
              video.onloadedmetadata = onLoaded;
              // Fallback in case onloadedmetadata doesn't fire quickly
              setTimeout(onLoaded, 500);
            });
            await video.play().catch((error) => {
              console.warn('[FileUpload] Video preview autoplay failed:', error);
            });

            const srcWidth = video.videoWidth || 1920;
            const srcHeight = video.videoHeight || 1080;
            // Downscale to reduce filesize
            const MAX_WIDTH = 1600;
            const MAX_HEIGHT = 1000;
            const scale = Math.min(1, MAX_WIDTH / srcWidth, MAX_HEIGHT / srcHeight);
            const width = Math.max(1, Math.round(srcWidth * scale));
            const height = Math.max(1, Math.round(srcHeight * scale));
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(video, 0, 0, width, height);

            const toBlob = (cnv, type, quality) => new Promise((resolve, reject) => {
              cnv.toBlob((b) => (b ? resolve(b) : reject(new Error('Failed to capture screenshot'))), type, quality);
            });

            // Prefer compressed JPEG; fall back to PNG if not allowed
            let blob;
            let fileName;
            let mimeType;
            try {
              const jpegBlob = await toBlob(canvas, 'image/jpeg', 0.72);
              const jpegFileProbe = new File([jpegBlob], `screenshot-${Date.now()}.jpg`, { type: 'image/jpeg' });
              const { isAllowed: jpegAllowed } = isFileAllowed(jpegFileProbe);
              if (jpegAllowed) {
                blob = jpegBlob;
                fileName = jpegFileProbe.name;
                mimeType = 'image/jpeg';
              } else {
                const pngBlob = await toBlob(canvas, 'image/png');
                const pngFileProbe = new File([pngBlob], `screenshot-${Date.now()}.png`, { type: 'image/png' });
                const { isAllowed: pngAllowed } = isFileAllowed(pngFileProbe);
                if (!pngAllowed) {
                  addToast(t('chatInput.files.screenshotsNotAllowed'), 'error');
                  return;
                }
                blob = pngBlob;
                fileName = pngFileProbe.name;
                mimeType = 'image/png';
              }
            } catch (e) {
              console.log('[ChatInput] Error capturing screenshot:', e);
              // As a last resort, try PNG without checks
              const pngBlob = await toBlob(canvas, 'image/png');
              blob = pngBlob;
              fileName = `screenshot-${Date.now()}.png`;
              mimeType = 'image/png';
            }

            // Stop tracks ASAP
            try {
              if (track) track.stop();
              stream.getTracks && stream.getTracks().forEach(t => t.stop());
            } catch (e) {
              console.log('[ChatInput] Error stopping tracks:', e);
            }

            const file = new File([blob], fileName, { type: mimeType });

            // Log capture details and sizes
            try {
              const blobMB = (blob.size / 1048576).toFixed(2);
              const fileMB = (file.size / 1048576).toFixed(2);
              console.log('[ChatInput] Screenshot capture:', { srcWidth, srcHeight, width, height, scale, mimeType, blobSizeBytes: blob.size, blobSizeMB: blobMB });
              console.log('[ChatInput] Screenshot file size:', file.size, 'bytes', `(${fileMB} MB)`);
            } catch (e) {
              console.log('[ChatInput] Error logging capture details:', e);
              console.log('[ChatInput] Screenshot capture:', { srcWidth, srcHeight, width, height, scale, mimeType, blobSizeBytes: blob.size, blobSizeMB: (blob.size / 1048576).toFixed(2) });
            }

            // Validate + upload via the shared placeholder flow (cleans up the
            // temp entry if the upload fails)
            await uploadFileWithPlaceholder({
              file,
              uploadFile,
              isFileAllowed,
              addToast,
              onAddUploadingFile,
              onAddFile,
              t,
              extraTempFields: { previewFile: file },
            });
          } catch (err) {
            if (err && (err.name === 'NotAllowedError' || err.name === 'AbortError')) {
              addToast(t('chatInput.files.screenCaptureCanceled'), 'default');
            } else {
              console.error('Error capturing screenshot:', err);
              addToast(err?.message || t('chatInput.files.screenshotCaptureFailed'), 'error');
            }
          } finally {
            close?.();
          }
        })();
        return;
      } else if (value === 'file-library') {
        // Open files modal with selection callback
        openModal('FilesModal', {
          onSelect: (files) => {
            if (Array.isArray(files)) {
              for (const file of files) {
                onAddFile?.(file);
              }
            } else {
              onAddFile?.(files);
            }
            // Close modal after selection
            closeModal();
          }
        });
        return;
      }
    }

    // Handle action parameter changes
    if (parameterData?.id === 'action') {
      // Update the placeholder when action changes
      setInputPlaceholder(value);
    }
    
    // Default behavior for other parameters
    handleValueChange(value);
  };

  const handleClearAction = (e) => {
    e.stopPropagation();
    if (parameterData?.id === 'action') {
      // Reset placeholder when action is cleared
      setInputPlaceholder(null);
    }
    handleValueChange(null);
  };
  
  const handleFileChange = async (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    
    // Check if user has credits before processing any files
    const { hasCredits, message: creditsMessage } = await hasCreditsForFileUpload();
    if (!hasCredits) {
      addToast(creditsMessage, 'error');
      // Reset the input value so the same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }
    
    await uploadFilesWithPlaceholders(selectedFiles, {
      uploadFile,
      isFileAllowed,
      addToast,
      onAddUploadingFile,
      onAddFile,
      t,
      extraTempFields: (file) => ({ previewFile: file }),
    });
    
    // Reset the input value so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // : todo :
  // if agent selected or theres a default agent
  //    and files are updated, remove the agent!

  // If no parameter data, just render a simple button
  if (!parameterData) {
    console.log('parameterData', parameterData);
    return (
      <ParameterButton
        icon={icon}
        text={text}
        iconColor={colors.text_color_tertiary}
        onPress={onPress}
        isHovered={isMainButtonHovered}
        onHoverIn={() => setIsMainButtonHovered(true)}
        onHoverOut={() => setIsMainButtonHovered(false)}
      />
    );
  }
  
  // For multiselect parameters
  if (parameterData?.ui?.component === 'multiselect' && parameterData?.validation?.options) {
    // On native, open the parameters modal instead of zeego dropdown (native menus can't render custom React components)
    if (Platform.OS !== 'web' && onOpenDetailModal) {
      return (
        <ParameterButton
          icon={icon}
          text={text}
          showValidationDot={showValidationDot}
          isHighlighted={isHighlighted}
          isHovered={isMainButtonHovered}
          onHoverIn={() => setIsMainButtonHovered(true)}
          onHoverOut={() => setIsMainButtonHovered(false)}
          onPress={() => onOpenDetailModal(parameterData)}
        />
      );
    }

    const currentValues = Array.isArray(paramValue) ? paramValue : (paramValue ? [paramValue] : []);

    const handleToggle = (optValue, close) => {
      const option = parameterData.validation.options.find(opt => opt.value === optValue);
      console.log("option:", option);

       // Check if option requires plus
      if (option?.flags?.isPlusOnly && !isPlus) {
        close?.(); // Close the dropdown
        openModal('PlusFeatureModal', { feature: 'search-web' });
        return;
      }

      // If tools are disabled due to capabilities, prevent toggling them.
      if (parameterData?.id === 'tools' && areTextToolsDisabled) {
        return;
      }
      
      let next;
      if (currentValues.includes(optValue)) {
        next = currentValues.filter(v => v !== optValue);
      } else {
        next = [...currentValues, optValue];
      }
      handleValueChange(next);
    };

    const getSubtitle = (option) => {
      if (parameterData?.id === 'tools' && areTextToolsDisabled) {
        return t('chatInput.tools.notAvailableForMedia');
      }
      return option?.description;
    };

    const shouldShowNewTooltip = Platform.OS === 'web' && parameterData?.id === 'tools' && shouldShowTooltipNoti(toolsNoti);

    return (
      <DropdownMenu.Root onOpenChange={(open) => {
          console.log('onPress', parameterData?.id);
          if (Platform.OS === 'web' && parameterData?.id === 'tools') {
            markTooltipNotiSeen(toolsNoti);
            if (!hasSeenNewToolsTooltip) setHasSeenNewToolsTooltip(true);
          }
      }}>
        <DropdownMenu.Trigger>
          <TriggerWithTooltip
            label={shouldShowNewTooltip ? LOCAL_TOOLTIP_NOTIS.TOOLS_NEW_10022025.title : displayName}
            tooltipProps={{
              delayDuration: shouldShowNewTooltip ? 0 : 500,
              alwaysVisible: shouldShowNewTooltip,
              isRed: shouldShowNewTooltip,
              side: shouldShowNewTooltip ? 'right' : 'top',
              labelSize: shouldShowNewTooltip ? 'xs' : 's',
              showIsXs: shouldShowNewTooltip,
            }}
          >
            <ParameterButton
              icon={icon}
              text={text}
              onHoverIn={() => setIsMainButtonHovered(true)}
              onHoverOut={() => setIsMainButtonHovered(false)}
              isHovered={isMainButtonHovered}
              showValidationDot={showValidationDot}
              isHighlighted={isHighlighted}
            />
          </TriggerWithTooltip>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content
          className={Platform.OS === 'web' ? 'DropdownMenuContent' : undefined}
          style={styles.dropdownContent}
        >
          {Platform.OS === 'web' && (
            <DropdownHeader
              displayName={displayName}
              isRequired={parameterData?.validation?.required}
              isRequirementValid={isRequirementValid}
            />
          )}

          {Platform.OS !== 'web' && parameterData.validation.options.map((option, i) => (
            <DropdownMenu.Item
              key={`${parameterData.id}-${option.value}-${i}`}
              disabled={parameterData?.id === 'tools' && areTextToolsDisabled}
              onSelect={() => handleToggle(option.value)}
            >
              {currentValues.includes(option.value) && (
                <DropdownMenu.ItemIcon ios={{ name: 'checkmark' }} />
              )}
              <DropdownMenu.ItemTitle>
                {option?.flags?.isPlusOnly && !isPlus ? `${option.label} (Plus)` : String(option.label ?? option.value)}
              </DropdownMenu.ItemTitle>
            </DropdownMenu.Item>
          ))}

          {Platform.OS === 'web' && parameterData.validation.options.map((option, i) => (
            <DropdownMenuItem
              key={`${parameterData.id}-${option.value}-${i}`}
              title={
                <View style={styles.titleContainer}>
                  <Text>{option.label}</Text>
                  {option?.flags?.isPlusOnly && !isPlus && (
                    <Badge
                      text="PLUS"
                      onPress={() => handleSelect(option.value)}
                      icon="sparkles"
                      size="small"
                      isPlus
                      isSmall
                      style={styles.titleContainerBadge}
                    />
                  )}
                </View>
              }
              subtitle={getSubtitle(option)}
              icon={currentValues.includes(option.value) ? (option.icon || 'check') : option.icon}
              isActive={currentValues.includes(option.value)}
              onSelect={(close) => handleToggle(option.value, close)}
              isDisabled={parameterData?.id === 'tools' && areTextToolsDisabled}
              showInactiveIcon={(option?.flags?.isPlusOnly && !isPlus) ? false : parameterData?.ui?.component === 'multiselect'}
              inactiveIcon={currentValues.includes(option.value) ? undefined : 'circle'}
            />
          ))}
          {shouldShowDone && Platform.OS === 'web' && (
            <DoneButton title={doneLabel} onSelect={() => {}} styles={DONE_BUTTON_MARGIN_TOP_STYLES} isSecondary />
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    );
  }

  // On native, dropdowns don't show; open the parameters modal (detail or full list) instead.
  const comp = parameterData?.ui?.component;
  if (Platform.OS !== 'web' && onOpenDetailModal && parameterData && PARAM_TYPES_USE_MODAL_ON_NATIVE.includes(comp)) {
    const openModalParam = (comp === 'file' || comp === 'image' || comp === 'audio') ? null : parameterData;
    return (
      <ParameterButton
        icon={parameterData?.ui?.icon || icon}
        text={null}
        showValidationDot={showValidationDot}
        isHighlighted={isHighlighted}
        isHovered={isMainButtonHovered}
        onHoverIn={() => setIsMainButtonHovered(true)}
        onHoverOut={() => setIsMainButtonHovered(false)}
        onPress={() => onOpenDetailModal(openModalParam)}
      />
    );
  }

  // For indicator parameters (visible, no dropdown)
  if (parameterData?.ui?.component === 'indicator') {
    const label = parameterData?.ui?.text || displayName || text;
    const clearParamId = parameterData?.ui?.clearParamId || parameterData?.id;
    const clearValue = parameterData?.ui?.clearValue;

    return (
      <ParameterButton
        icon={parameterData?.ui?.icon || icon}
        text={label}
        isIndicator={true}
        onPress={() => {
          // Clear the target parameter
          if (parameterData?.id === 'action') {
            setInputPlaceholder(null);
          }

          // Compute next value based on existing selection and optional clearValue
          let nextValue = null;
          const existing = selectedParameters?.[clearParamId];
          if (clearValue !== undefined) {
            if (Array.isArray(existing)) {
              const filtered = existing.filter(v => v !== clearValue);
              nextValue = filtered.length ? filtered : null;
            } else if (existing === clearValue) {
              nextValue = null;
            } else {
              nextValue = existing ?? null;
            }
          } else {
            nextValue = null;
          }

          if (onChange) {
            onChange(clearParamId, nextValue);
          } else {
            onPress?.(clearParamId, nextValue);
          }
        }}
        isHovered={isMainButtonHovered}
        onHoverIn={() => setIsMainButtonHovered(true)}
        onHoverOut={() => setIsMainButtonHovered(false)}
      />
    );
  }
  
  // For parameters with options (select type)
  if (['select', 'action'].includes(parameterData?.ui?.component) && parameterData?.validation?.options) {
    // On native, open the parameters modal instead of zeego dropdown (native menus can't render custom React components)
    if (Platform.OS !== 'web' && onOpenDetailModal) {
      const openModalParam = (parameterData?.id === 'files') ? null : parameterData;
      return (
        <ParameterButton
          icon={parameterData?.ui?.icon || icon}
          text={parameterData?.ui?.text || text}
          showValidationDot={showValidationDot}
          isHighlighted={isHighlighted}
          isHovered={isMainButtonHovered}
          onHoverIn={() => setIsMainButtonHovered(true)}
          onHoverOut={() => setIsMainButtonHovered(false)}
          onPress={() => onOpenDetailModal(openModalParam)}
        />
      );
    }

    const isAction = parameterData?.id === 'action';
    const showActionText = !isAction || !selectedParameters?.action;
    const hasFiles = files.length > 0;
    const hasSelectedAgents = selectedAgentIds?.length > 0;

    const hasTextCapableModel = selectedModels.some(model => 
      model?.capabilities?.output?.hasText
    );

    const getDisabledState = (option) => {
      if (isFilesButtonDisabled) return true;
      if (hasSelectedAgents && option.value !== 'create-image') return true; // Only allow create-image with agents
      
      // Check if any selected model supports image output for create-image action
      if (option.value === 'create-image' && selectedModels?.length > 0) {
        const hasImageCapableModel = selectedModels.some(model => 
          model?.capabilities?.output?.hasImages
        );
        if (!hasImageCapableModel) return true;
      }

      // Check if any selected model supports text output for text-based actions
      if (['search'].includes(option.value) && selectedModels?.length > 0 && isAction) {
        if (!hasTextCapableModel) {
          return true;
        }
      }
      // Disable screenshot option if unsupported
      if (parameterData?.id === 'files' && option.value === 'file-screenshot') {
        if (!canUseScreenCapture) return true;
      }
      
      if (hasFiles) return ['create-image', 'search'].includes(option.value);
      return false;
    };

    const getSubtitle = (option) => {
      if (isAction && getDisabledState(option) && option.value === 'search') {
        if (hasFiles) return t('chatInput.tools.removeAttachmentsToSearch');
        else if (hasSelectedAgents) return undefined;
        else if (selectedModels?.length > 0 && !selectedModels.some(model => model?.capabilities?.output?.hasText)) {
          return t('chatInput.tools.modelsNoTextSupport');
        }
        return t('chatInput.tools.unavailableInContext');
      }
      if (isAction && getDisabledState(option) && option.value === 'create-image') {
        if (hasFiles) return t('chatInput.tools.removeAttachmentsForImage');
        else if (hasSelectedAgents) return undefined;
        else if (selectedModels?.length > 0 && !selectedModels.some(model => model?.capabilities?.output?.hasImages)) {
          return t('chatInput.tools.modelsNoImageSupport');
        }
        return t('chatInput.tools.unavailableInContext');
      }
      if (parameterData?.id === 'files' && option.value === 'file-screenshot' && getDisabledState(option)) {
        if (hasReachedFileLimit) return '';
        return t('chatInput.files.screenCaptureNotSupported');
      }
      if (parameterData?.id === 'files' && option.value === 'file-upload') {
        return t('chatInput.files.uploadSubtitle');
      }
      // if (isAction && getDisabledState(option) && ['write-code', 'run-deep-research'].includes(option.value)) {
      //   if (selectedModels?.length > 0 && !selectedModels.some(model => model?.capabilities?.output?.hasText)) {
      //     return "Selected models don't support text generation";
      //   }
      //   return "Tool unavailable in the current context";
      // }
      return undefined;
    };

    // : todo :
    // only allow agents if models support text output... for now

    // : todo :
    // allow if agent has file/image input capablities 

    // Check if files button should be disabled based on selected action or agents
    const isFilesButtonDisabled = (parameterData?.id === 'files' && 
      ((['create-image', 'search'].includes(selectedParameters?.action)) || hasSelectedAgents || hasReachedFileLimit));

    const getAllDisabledTitle = () => {
      if (isFilesButtonDisabled && parameterData?.id === 'files') {
        if (hasReachedFileLimit) return t('chatInput.maxFilesPerMessage').replace('%MAX%', MAX_FILES_UPLOADED_IN_MESSAGE);
        return t('chatInput.files.attachmentsUnavailable');
      }
      if (isAction) return t('chatInput.tools.toolsUnavailable');
      return t('chatInput.tools.parametersUnavailable');
    };

    const allOptionsDisabled = parameterData.validation.options.every(getDisabledState);

    return (
      <>
        <View style={styles.selectedActionContainer}>
          <DropdownMenu.Root>
            <DropdownMenu.Trigger>
              <TriggerWithTooltip label={displayName}>
                <ParameterButton
                  icon={icon}
                  text={showActionText ? text : null}
                  iconColor={(isFilesButtonDisabled || (hasSelectedAgents && parameterData?.id === 'files' && selectedParameters?.action !== 'create-image')) ? colors.text_color_tertiary : colors.text_color_primary}
                  textColor={(isFilesButtonDisabled || (hasSelectedAgents && parameterData?.id === 'files' && selectedParameters?.action !== 'create-image')) ? 'tertiary' : 'primary'}
                  disabled={isFilesButtonDisabled || hasSelectedAgents}
                  showValidationDot={showValidationDot}
                  isHighlighted={isHighlighted}
                  isHovered={isMainButtonHovered}
                  onHoverIn={() => setIsMainButtonHovered(true)}
                  onHoverOut={() => setIsMainButtonHovered(false)}
                />
              </TriggerWithTooltip>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content
              className={Platform.OS === 'web' ? 'DropdownMenuContent' : undefined}
              style={styles.dropdownContent}
            >
              {Platform.OS === 'web' && (
                <DropdownHeader
                  displayName={displayName}
                  isRequired={parameterData?.validation?.required}
                  isRequirementValid={isRequirementValid}
                  isDisabled={allOptionsDisabled}
                />
              )}
              {Platform.OS !== 'web' && parameterData.validation.options.map((option, i) => (
                <DropdownMenu.Item
                  key={`cicpt-native-${option?.value}-${i}`}
                  disabled={getDisabledState(option)}
                  onSelect={() => handleSelect(option.value)}
                >
                  {parameterData?.ui?.component !== 'action' && paramValue === option.value && (
                    <DropdownMenu.ItemIcon ios={{ name: 'checkmark' }} />
                  )}
                  <DropdownMenu.ItemTitle>
                    {option?.flags?.isPlusOnly && !isPlus ? `${option.label} (Plus)` : String(option.label ?? option.value)}
                  </DropdownMenu.ItemTitle>
                </DropdownMenu.Item>
              ))}
              {Platform.OS === 'web' && parameterData.validation.options.map((option, i) => {
                return (
                  <React.Fragment key={`cicpt-${option?.value}-${i}`}>
                    {allOptionsDisabled && i === 0 && (
                      <>
                        <DropdownMenuItem
                          key={`${option.value}-disabled`}
                          title={getAllDisabledTitle()}
                          isDisabled={true}
                        />
                        <DropdownMenuItemDivider />
                      </>
                    )}
                    <DropdownMenuItem
                      key={option.value}
                      title={
                        <View style={styles.titleContainer}>
                          <Text>{option.label}</Text>
                          {option?.flags?.isPlusOnly && !isPlus && (
                            <Badge
                              text="PLUS"
                              onPress={() => handleSelect(option.value)}
                              icon="sparkles"
                              size="small"
                              isPlus
                              isSmall
                              style={styles.titleContainerBadge}
                            />
                          )}
                        </View>
                      }
                      subtitle={getSubtitle(option)}
                      icon={option.icon || undefined}
                      isActive={parameterData?.ui?.component === 'action' ? false : paramValue === option.value}
                      onSelect={(close) => handleSelect(option.value, close)}
                      showInactiveIcon={(option?.flags?.isPlusOnly && !isPlus) ? false :parameterData?.ui?.component === 'select'}
                      inactiveIcon={getDisabledState(option) && parameterData?.ui?.component === 'select' ? 'circle-x' : undefined}
                      isDisabled={getDisabledState(option)}
                    />
                  </React.Fragment>
                )
              })}
              {shouldShowDone && Platform.OS === 'web' && (
                <DoneButton title={doneLabel} onSelect={() => {}} styles={styles} isSecondary />
              )}
            </DropdownMenu.Content>
          </DropdownMenu.Root>

          {/* Add vertical divider before selected action button */}
          {selectedActionOption && (
            <View style={styles.verticalDivider} />
          )}

          {/* Show selected action as a separate button */}
          {selectedActionOption && (
            <ParameterButton
              icon={selectedActionOption.icon}
              text={selectedActionOption.label}
              showXmark={true}
              onPress={handleClearAction}
              isHovered={isSelectedActionHovered}
              onHoverIn={() => setIsSelectedActionHovered(true)}
              onHoverOut={() => setIsSelectedActionHovered(false)}
            />
          )}
        </View>
        {Platform.OS === 'web' && (
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
          style={WEB_HIDDEN_INPUT_STYLE}
            multiple
          />
        )}
      </>
    );
  }
  
  // For number/slider type parameters
  if (parameterData?.ui?.component === 'number' || parameterData?.ui?.component === 'slider') {
    return (
      <DropdownMenu.Root>
        <DropdownMenu.Trigger>
          <TriggerWithTooltip label={displayName}>
            <ParameterButton
              icon={icon}
              text={text}
              showValidationDot={showValidationDot}
              isHighlighted={isHighlighted}
              isHovered={isMainButtonHovered}
              onHoverIn={() => setIsMainButtonHovered(true)}
              onHoverOut={() => setIsMainButtonHovered(false)}
            />
          </TriggerWithTooltip>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content className="DropdownMenuContent" style={styles.dropdownContent}>
          <DropdownHeader
            displayName={displayName}
            isRequired={parameterData?.validation?.required}
            isRequirementValid={isRequirementValid}
          />
          
          <NumberControl
            param={{...parameterData, value: paramValue}}
            onChange={handleValueChange}
          />
          {shouldShowDone && (
            <DoneButton title={doneLabel} onSelect={() => {}} styles={styles} isSecondary />
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    );
  }
  
  // For file upload parameters
  if (parameterData?.ui?.component === 'file' || parameterData?.ui?.component === 'image' || parameterData?.ui?.component === 'audio') {
    return (
      <DropdownMenu.Root>
        <DropdownMenu.Trigger>
          <TriggerWithTooltip label={displayName}>
            <ParameterButton
              icon={icon}
              text={text}
              isHovered={isMainButtonHovered}
              onHoverIn={() => setIsMainButtonHovered(true)}
              onHoverOut={() => setIsMainButtonHovered(false)}
            />
          </TriggerWithTooltip>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content className="DropdownMenuContent" style={styles.dropdownContent}>
          <DropdownHeader
            displayName={displayName}
            isRequired={parameterData?.validation?.required}
            isRequirementValid={isRequirementValid}
          />
          
          <FileUploadControl
            param={parameterData}
          />
          {shouldShowDone && (
            <DoneButton title={doneLabel} onSelect={() => {}} styles={styles} isSecondary />
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    );
  }
  
  // For toggle/boolean parameters
  if (parameterData?.ui?.component === 'toggle') {
    const booleanValue = typeof text === 'boolean' ? text : false;
    return (
      <DropdownMenu.Root>
        <DropdownMenu.Trigger>
          <TriggerWithTooltip label={displayName}>
            <ParameterButton
              icon={icon}
              text={text}
              isToggle={true}
              showValidationDot={showValidationDot}
              isHighlighted={isHighlighted}
              isHovered={isMainButtonHovered}
              onHoverIn={() => setIsMainButtonHovered(true)}
              onHoverOut={() => setIsMainButtonHovered(false)}
            />
          </TriggerWithTooltip>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content className="DropdownMenuContent" style={styles.dropdownContent}>
          <DropdownHeader
            displayName={displayName}
            isRequired={parameterData?.validation?.required}
            isRequirementValid={isRequirementValid}
          />
          
          <ToggleControl
            param={{...parameterData, value: paramValue}}
            onChange={handleValueChange}
          />
          <View style={styles.doneButtonContainerSmall}>
            <DoneButton title={doneLabel} onSelect={() => {}} styles={styles} isSecondary />
          </View>
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    );
  }
  
  // For textarea parameters
  if (parameterData?.ui?.component === 'textarea') {
    return (
      <DropdownMenu.Root>
        <DropdownMenu.Trigger>
          <TriggerWithTooltip label={displayName}>
            <ParameterButton
              icon={icon}
              showValidationDot={showValidationDot}
              isHighlighted={isHighlighted}
              isHovered={isMainButtonHovered}
              onHoverIn={() => setIsMainButtonHovered(true)}
              onHoverOut={() => setIsMainButtonHovered(false)}
            />
          </TriggerWithTooltip>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content className="DropdownMenuContent" style={styles.dropdownContent}>
          <DropdownHeader
            displayName={displayName}
            description={parameterData.ui.description || parameterData.description}
            isRequired={parameterData?.validation?.required}
            isRequirementValid={isRequirementValid}
          />
          
          <TextareaControl
            param={{...parameterData, value: paramValue}}
            onChange={handleValueChange}
          />
          <DoneButton title={doneLabel} onSelect={() => {}} styles={styles} isSecondary />
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    );
  }
  
  // For date range parameters
  if (parameterData?.ui?.component === 'daterange') {
    return (
      <DropdownMenu.Root>
        <DropdownMenu.Trigger>
          <TriggerWithTooltip label={displayName}>
            <ParameterButton
              icon={icon}
              text={typeof text === 'string' ? text : null}
              showValidationDot={showValidationDot}
              isHighlighted={isHighlighted}
              isHovered={isMainButtonHovered}
              onHoverIn={() => setIsMainButtonHovered(true)}
              onHoverOut={() => setIsMainButtonHovered(false)}
            />
          </TriggerWithTooltip>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content className="DropdownMenuContent" style={styles.dropdownContent}>
          <DropdownHeader
            displayName={displayName}
            isRequired={parameterData?.validation?.required}
            isRequirementValid={isRequirementValid}
            isTight={true}
          />
          <DateRangeControl
            param={{ ...parameterData, value: paramValue }}
            onChange={handleValueChange}
          />
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    );
  }

  // For textarea-multi parameters
  if (parameterData?.ui?.component === 'textarea-multi') {
    return (
      <DropdownMenu.Root>
        <DropdownMenu.Trigger>
          <TriggerWithTooltip label={displayName}>
            <ParameterButton
              icon={icon}
              text={typeof text === 'string' ? text : null}
              showValidationDot={showValidationDot}
              isHighlighted={isHighlighted}
              isHovered={isMainButtonHovered}
              onHoverIn={() => setIsMainButtonHovered(true)}
              onHoverOut={() => setIsMainButtonHovered(false)}
            />
          </TriggerWithTooltip>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content className="DropdownMenuContent" style={styles.dropdownContent}>
          <DropdownHeader
            displayName={displayName}
            isRequired={parameterData?.validation?.required}
            isRequirementValid={isRequirementValid}
          />          
          <TextareaMultiControl
            param={{...parameterData, value: paramValue || {}}}
            onChange={handleValueChange}
          />
          
          <DoneButton title={t('common.done')} onSelect={() => {}} styles={styles} isSecondary />
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    );
  }
  
  // Default fallback for other parameter types
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger>
        <TriggerWithTooltip label={displayName}>
          <ParameterButton
            icon={icon}
            text={text}
            isHovered={isMainButtonHovered}
            onHoverIn={() => setIsMainButtonHovered(true)}
            onHoverOut={() => setIsMainButtonHovered(false)}
          />
        </TriggerWithTooltip>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content className="DropdownMenuContent" style={styles.dropdownContent}>
        <DropdownHeader
          displayName={displayName}
          isRequired={parameterData?.validation?.required}
          isRequirementValid={isRequirementValid}
        />
        
        <DropdownMenuItem
          title={t('chatInput.tools.notSupported')}
          icon="exclamation-circle"
          onSelect={() => {}}
        />
        {shouldShowDone && (
          <DoneButton title={doneLabel} onSelect={() => {}} styles={styles} isSecondary />
        )}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
};

export default ChatInputCustomParameterButton;